import { useQueryClient } from '@tanstack/react-query'
import { FaqArticlePayload, FaqPageLocationState } from 'Modules'
import React from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import Breadcrumb from 'component/breadcrumb/breadcrumb'
import { BREADCRUMBS } from 'constants/breadcrumb'
import { faqCategoryKeys, useCreateFaqArticle, useUnsavedChangesGuard } from 'hooks/faq'
import { DiscardChangesModal, FaqArticleForm } from 'modules/faq'
import { PAGES } from 'routes'
import { getApiErrorCode, getApiErrorMessage } from 'utils/api-error'

import '../style.scss'

const FaqArticleCreatePage: React.FC = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const createArticle = useCreateFaqArticle()
  const { setDirty, requestLeave, discardModalProps } = useUnsavedChangesGuard()

  const handleSubmit = async (payload: FaqArticlePayload) => {
    try {
      const created = await createArticle.mutateAsync(payload)
      toast.success('Create article successfully')
      const state: FaqPageLocationState = {
        expandCategoryId: created?.categoryId ?? payload.categoryId,
      }
      navigate(PAGES.FAQ, { state })
    } catch (error: unknown) {
      if (getApiErrorCode(error) === 'FAQ_ARTICLE_CATEGORY_FULL') {
        toast.error('This category already has 20 articles')
        void queryClient.invalidateQueries({ queryKey: faqCategoryKeys.lists() })
      } else {
        toast.error(getApiErrorMessage(error, 'Failed to create article'))
      }
    }
  }

  return (
    <div className="main-section faq-management-page faq-article-page">
      <Breadcrumb
        additionalItems={[BREADCRUMBS.FAQ, BREADCRUMBS.FAQ_ARTICLE_CREATE]}
        onNavigate={requestLeave}
      />
      <div className="main-title">Create Article</div>

      <FaqArticleForm
        submitting={createArticle.isPending}
        onSubmit={(payload) => void handleSubmit(payload)}
        onCancel={() => requestLeave(PAGES.FAQ)}
        onDirtyChange={setDirty}
      />

      <DiscardChangesModal {...discardModalProps} />
    </div>
  )
}

export default FaqArticleCreatePage
